import { Camera } from 'domain/camera'
import { useAuth } from 'layout/context/auth-context'
import { useToast } from 'layout/context/toast-context'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Toolbar } from 'primereact/toolbar'
import React, { useState } from 'react'
import { useCamera } from '../hooks/useCamera'
import { useCameraModel } from '../hooks/useCameraModel'
import { emptyCamera } from '../types/const'
import CameraStepper from './camera-stepper'
import './cameras-view.scss'
import DeleteCameraDialog from './delete-camera-dialog'

const CamerasView = () => {
  const { showSuccess, showError } = useToast()
  const [camera, setCamera] = useState<Camera>(emptyCamera)
  const [submitted, setSubmitted] = useState(false)
  const [cameraDialog, setCameraDialog] = useState(false)
  const [deleteCameraDialog, setDeleteCameraDialog] = useState(false)

  const {
    cameras,
    isLoading,
    fetchCameras,
    createCamera,
    updateCamera,
    deleteCamera,
  } = useCamera()

  const { cameraModels, fetchCameraModels } = useCameraModel()

  const { clientId } = useAuth()

  React.useEffect(() => {
    fetchCameraModels()
  }, [fetchCameraModels])

  React.useEffect(() => {
    if (clientId) {
      fetchCameras({ clientId })
    }
  }, [clientId, fetchCameras])

  const openNew = () => {
    if (clientId) {
      setCamera({ ...emptyCamera, clientId })
      setSubmitted(false)
      setCameraDialog(true)
    }
  }

  const hideDialog = () => {
    setSubmitted(false)
    setCameraDialog(false)
  }

  const hideDeleteCameraDialog = () => {
    setDeleteCameraDialog(false)
  }

  const saveCamera = async () => {
    setSubmitted(true)
    if (!camera.name.trim() || !camera.ipAddress.trim()) return

    if (camera.id) {
      const { password, ...rest } = camera
      await updateCamera(camera.id, { ...rest, ...(password ? { password } : {}) })
    } else {
      await createCamera({ ...camera, id: '', isActive: true })
    }

    setCameraDialog(false)
    setCamera(emptyCamera)
  }

  const editCamera = (camera: Camera) => {
    setCamera({ ...camera })
    setCameraDialog(true)
  }

  const confirmDeleteCamera = (camera: Camera) => {
    setCamera(camera)
    setDeleteCameraDialog(true)
  }

  const deleteCameraItem = async () => {
    const success = await deleteCamera(camera.id)

    if (success) {
      showSuccess('Sucesso', 'Câmera excluída com sucesso')
      setDeleteCameraDialog(false)
      setCamera(emptyCamera)
    } else {
      showError('Erro', 'Falha ao excluir câmera')
    }
  }

  const leftToolbarTemplate = () => {
    return (
      <div className='cameras-toolbar-left'>
        <Button
          label='Nova Câmera'
          icon='pi pi-plus'
          className='cameras-btn-new'
          onClick={openNew}
          disabled={!clientId}
        />
      </div>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <div className='cameras-toolbar-right'>
        {clientId && (
          <div className='cameras-stats'>
            <span className='cameras-stats-text'>Total de Câmeras</span>
            <span className='cameras-badge'>{cameras.length}</span>
          </div>
        )}
      </div>
    )
  }

  const actionBodyTemplate = (rowData: Camera) => {
    return (
      <div className='cameras-actions'>
        <Button
          icon='pi pi-pencil'
          rounded
          className='cameras-action-edit'
          onClick={() => editCamera(rowData)}
          tooltip='Editar'
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon='pi pi-trash'
          rounded
          severity='danger'
          className='cameras-action-delete'
          onClick={() => confirmDeleteCamera(rowData)}
          tooltip='Excluir'
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    )
  }

  const httpsBodyTemplate = (rowData: Camera) => {
    return (
      <span
        className={`cameras-protocol-badge ${rowData.isHttps ? 'cameras-protocol-https' : 'cameras-protocol-http'
          }`}
      >
        {rowData.isHttps ? 'HTTPS' : 'HTTP'}
      </span>
    )
  }

  const cameraTypeBodyTemplate = (rowData: Camera) => {
    return (
      <span className='cameras-protocol-badge'>
        {rowData.model?.name ?? '-'}
      </span>
    )
  }

  const nameBodyTemplate = (rowData: Camera) => {
    return (
      <div className='cameras-text-cell'>
        <i className='pi pi-camera cameras-icon-small' />
        <span>{rowData.name}</span>
      </div>
    )
  }

  const isActiveBodyTemplate = (rowData: Camera) => {
    return (
      <span
        className={`cameras-protocol-badge ${rowData.isActive === true
          ? 'cameras-active-true'
          : 'cameras-active-false'
          }`}
      >
        {rowData.isActive === true ? 'SIM' : 'NÃO'}
      </span>
    )
  }

  const ipBodyTemplate = (rowData: Camera) => {
    return (
      <div className='cameras-text-cell'>
        <i className='pi pi-globe cameras-icon-small' />
        <span>
          {rowData.ipAddress}:{rowData.httpPort}
        </span>
      </div>
    )
  }

  return (
    <div className='cameras-container'>
      <div className='cameras-header'>
        <div className='cameras-title-section'>
          <i className='pi pi-video cameras-main-icon' />
          <h1 className='cameras-title'>Gerenciamento de Câmeras</h1>
        </div>
        <div className='cameras-subtitle'>
          Configure e monitore suas câmeras de segurança
        </div>
      </div>

      <Card className='cameras-card'>
        <Toolbar
          className='cameras-toolbar'
          left={leftToolbarTemplate}
          right={rightToolbarTemplate}
        />

        <DataTable
          value={cameras}
          dataKey='id'
          paginator
          loading={isLoading}
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          className='cameras-datatable'
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          currentPageReportTemplate='Mostrando {first} a {last} de {totalRecords} câmeras'
          emptyMessage='Nenhuma câmera encontrada para este cliente.'
          responsiveLayout='scroll'
          stripedRows
          showGridlines
        >
          <Column
            field='name'
            header='Nome'
            sortable
            body={nameBodyTemplate}
            headerStyle={{ minWidth: '15rem' }}
            className='cameras-name-column'
          />
          <Column
            field='ipAddress'
            header='Endereço IP'
            sortable
            body={ipBodyTemplate}
            headerStyle={{ minWidth: '15rem' }}
            className='cameras-ip-column'
          />
          <Column
            field='isHttps'
            header='Protocolo'
            body={httpsBodyTemplate}
            sortable
            headerStyle={{ minWidth: '8rem' }}
            className='cameras-protocol-column'
          />
          <Column
            field='model.name'
            header='Modelo'
            body={cameraTypeBodyTemplate}
            sortable
            headerStyle={{ minWidth: '8rem' }}
            className='cameras-protocol-column'
          />

          <Column
            body={isActiveBodyTemplate}
            header='Ativo'
            sortable
            headerStyle={{ minWidth: '3rem' }}
          />
          <Column
            body={actionBodyTemplate}
            header='Ações'
            headerStyle={{ minWidth: '10rem' }}
            className='cameras-actions-column'
          />
        </DataTable>

        <CameraStepper
          camera={camera}
          models={cameraModels}
          onHide={hideDialog}
          setCamera={setCamera}
          isLoading={isLoading}
          visible={cameraDialog}
          saveCamera={saveCamera}
          submitted={submitted}
        />

        <DeleteCameraDialog
          camera={camera}
          deleteCamera={deleteCameraItem}
          onHide={hideDeleteCameraDialog}
          visible={deleteCameraDialog}
        />
      </Card>
    </div>
  )
}

export default CamerasView
